import React, { FormEvent, useEffect, useState } from 'react';
import PageWrapper from '../../components/ui/layout/PageWrapper';
import PaymentDetailsForm from '../../components/payment-details-form/PaymentDetailsForm';
import { SPREEDLY_TEST_CREDIT_CARD, SPREEDLY_TEST_CVV, SPREEDLY_TEST_EXP_MONTH, SPREEDLY_TEST_EXP_YEAR } from '../../utils/constants';
import Button from '../../components/ui/button/Button';
import Panel from '../../components/ui/panel/Panel';
import { Col, Row } from 'react-grid-system';
import Input from '../../components/ui/input/Input';
import counterpart from 'counterpart';
import FormGroup from '../../components/ui/form-group/FormGroup';
import FormLabel from '../../components/ui/form-label/FormLabel';
import { CustomerServiceClient } from '../../utils/grpc/generated/Billsby.Protos/billing/private/customer/CustomerServiceClientPb';
import { validatePaymentDetailsForm } from '../../components/payment-details-form/PaymentDetailsForm';
import { ISpreedlyCardData, IPaymentDetailsError } from '../../models/Payment';
import { ConfigConstants } from '../../utils/config';
import { grpcUnaryCall } from '../../utils/grpc/grpcUtils';
import ErrorNotification from '../../components/ui/error-notification/ErrorNotification';
import NavigationAlert from '../../components/ui/navigation-alert/NavigationAlert';
import { Link } from 'react-router-dom';
import Text from '../../components/ui/text/Text';
import "./ChangeCard.scss";
import { extractGrpcErrorMessage } from '../../utils/commonUtils';
import moment from 'moment';
import NoticePanel from '../../components/ui/notice-panel/NoticePanel';
import CollapsablePanelWithArrow from '../../components/ui/collapsable-panel-with-arrow/CollapsablePanelWithArrow';
import { AdminUpdateCustomerCardDetailsScaRequest, UpdateCustomerPaymentDetailsScaRequest } from '../../utils/grpc/generated/Billsby.Protos/billing/private/customer/customer_pb';

interface IChangeCardState {
  cardholderName: string,
  expiryMonth: number,
  expiryYear: number,
  cardDetailsErrors: IPaymentDetailsError | null,
  apiError: string,
  isSuccess: boolean,
  customerUniqueID?: string,
  companyID?: number,
  isLoading: boolean,
  isStripeTestCodesCollapsed: boolean
}

/**
 * component used just in dev and test to change cards
 * @returns 
 */
const ChangeCard: React.FC = () => {

  const CARD_EXPIRY_YEAR_FUTURE = moment().year() + 2;
  const CARD_EXPIRY_MONTH_FUTURE = 12;

  const [state, setState] = useState<IChangeCardState>({
    cardholderName: "",
    expiryMonth: 0,
    expiryYear: 0,
    cardDetailsErrors: null,
    apiError: "",
    isSuccess: false,
    customerUniqueID: undefined,
    companyID: undefined,
    isLoading: false,
    isStripeTestCodesCollapsed: false
  })

  const { cardholderName, expiryMonth, expiryYear, cardDetailsErrors, customerUniqueID, companyID,
    isLoading, apiError, isSuccess, isStripeTestCodesCollapsed } = state;
  const paymentDetailsFormData = { cardholderName, expiryMonth, expiryYear };

  const onSubmit = (evt: FormEvent) => {
    evt.preventDefault();
    //setState({ ...state, isLoading: true, isSuccess: false });

    setState((prevState) => ({ ...prevState, isLoading: true, isSuccess: false }));

    // we're using a trick to create always a valid token which is using an expiry date in the future regardless
    // of the inputted date, then we will send the original date to the backend, i this way we can replace a card even with an expired one
    validatePaymentDetailsForm({ cardholderName, expiryMonth: CARD_EXPIRY_MONTH_FUTURE, expiryYear: CARD_EXPIRY_YEAR_FUTURE },
      {
        addressLine1: "",
        addressLine2: "",
        state: "",
        city: "",
        country: "",
        postCode: ""
      },
      async (cardData: ISpreedlyCardData, error: IPaymentDetailsError | null) => {
        if (error || !cardData) {
          // use functional approach when setting a state here because we have a multiple based state, using setState({ ...state, cardDetailsError }) 
          // would be wrong because the operation is async and state might be not consolidated yet
          setState((prevState) => ({ ...prevState, cardDetailsErrors: error, isLoading: false }));
          return;
        }

        const { card_type, last_four_digits, token } = cardData;

        setState((prevState) => ({ ...prevState, cardDetailsErrors: null, apiError: "" }));

        try {
          const adminUpdateCustomerCardDetailsRequest = new AdminUpdateCustomerCardDetailsScaRequest();
          adminUpdateCustomerCardDetailsRequest.setCustomerUniqueId(String(customerUniqueID));
          adminUpdateCustomerCardDetailsRequest.setCompanyId(Number(companyID));
          adminUpdateCustomerCardDetailsRequest.setCardType(card_type);
          adminUpdateCustomerCardDetailsRequest.setCardLastFourDigits(last_four_digits);
          adminUpdateCustomerCardDetailsRequest.setExpiryMonth(+expiryMonth);
          adminUpdateCustomerCardDetailsRequest.setExpiryYear(+expiryYear);
          adminUpdateCustomerCardDetailsRequest.setPaymentCardToken(token);
          adminUpdateCustomerCardDetailsRequest.setFullName(cardholderName);
          adminUpdateCustomerCardDetailsRequest.setIpAddress("");
          adminUpdateCustomerCardDetailsRequest.setTransactionToken("");
          adminUpdateCustomerCardDetailsRequest.setBrowserInfo("");
          adminUpdateCustomerCardDetailsRequest.setRedirectUrl("");
          adminUpdateCustomerCardDetailsRequest.setCallbackUrl("");
          const customerServiceClient = new CustomerServiceClient(ConfigConstants.grpcBaseUrl);
          const response = await grpcUnaryCall(adminUpdateCustomerCardDetailsRequest, customerServiceClient, customerServiceClient.adminUpdateCustomerCardDetailsSca);
          if ((response as any).error) {
            throw (response as any).error;
          }
          else {
            setState((prevState) => ({ ...prevState, apiError: "", isSuccess: true, cardDetailsErrors: null, isLoading: false }));
          }
        }
        catch (err) {
          console.log(err);
          setState((prevState) => ({ ...prevState, apiError: extractGrpcErrorMessage(err.message) || err.messsage, isLoading: false }));
        }
      });
  }

  return (
    <PageWrapper className="change-card">
      <FormGroup>
        <NavigationAlert
          className="change-card__warning"
          title="CHANGE_CARD_WARNING_TITLE"
          subTitle={
            <>
              <Text
                content="CHANGE_CARD_WARNING_SUBTITLE"
                component="div"
                translateWith={{
                  users: <Link to="/companies">{counterpart("NAVBAR_COMPANIES").toLowerCase()}</Link>
                }}
              />
              <Text
                content="CHANGE_CARD_WARNING_SUBTITLE2"
                component="div"
                translateWith={{
                  testData: <a href="https://docs.spreedly.com/reference/test-data/" target="_blank" rel="noopener noreferrer">{counterpart("CHANGE_CARD_WARNING_SPREEDLY_TEST_DATA")}</a>
                }}
                noMargin
              />

              <Text
                content="CHANGE_CARD_WARNING_SUBTITLE3"
                component="div"
                translateWith={{
                  icon: <i className="far fa-check"></i>
                }}
                noMargin
              />
              <Text
                content="CHANGE_CARD_WARNING_SUBTITLE4"
                component="div"
                translateWith={{
                  icon: <i className="far fa-times" />
                }}
                noMargin
              />

            </>
          }
          shouldTranslateSubTitle={false}
          buttonLabel=""
          onClick={() => { }}
        />
      </FormGroup>

      <CollapsablePanelWithArrow
        onClick={() => setState(prevState => ({ ...prevState, isStripeTestCodesCollapsed: !isStripeTestCodesCollapsed }))}
        isCollapsed={isStripeTestCodesCollapsed}
        title="CHANGE_CARD_STRIPE_TEST_CODES"
      >
        <Text content="4000000000000002	Charge is declined with a card_declined code." shouldTranslate={false} noMargin />
        <Text content="4000000000009995	Charge is declined with a card_declined code. The decline_code attribute is insufficient_funds." shouldTranslate={false} noMargin />
        <Text content="4000000000009987	Charge is declined with a card_declined code. The decline_code attribute is lost_card." shouldTranslate={false} noMargin />
        <Text content="4000000000009979	Charge is declined with a card_declined code. The decline_code attribute is stolen_card." shouldTranslate={false} noMargin />
        <Text content="4000000000000069	Charge is declined with an expired_card code." shouldTranslate={false} noMargin />
        <Text content="4000000000000127	Charge is declined with an incorrect_cvc code." shouldTranslate={false} noMargin />
        <Text content="4000000000000119	Charge is declined with a processing_error code." shouldTranslate={false} />
        <a href="https://stripe.com/docs/testing" rel="noopener noreferrer" target="_blank">{counterpart("CHANGE_CARD_STRIPE_DOCUMENTATION")}</a>
      </CollapsablePanelWithArrow>

      {isSuccess && !isLoading && <NoticePanel
        type="success"
        icon="far fa-check-circle"
        content="CHANGE_CARD_UPDATE_TEST_CARD_SUCCESS"
      />}
      {
        (!!apiError || (!!cardDetailsErrors && Object.values(cardDetailsErrors).some(el => el))) && !isLoading
        && <NoticePanel
          type="error"
          icon="far fa-exclamation-circle"
          content={apiError || counterpart("CHANGE_CARD_GENERIC_ERROR")}
          shouldTranslate={false}
        />
      }
      <Panel title="CHANGE_CARD_MANUALLY_INPUT_PAYMENT_DETAILS">
        <form onSubmit={onSubmit}>
          <FormGroup>
            <Row align="center">
              <Col md={2}>
                <FormLabel target="company-id" content="CHANGE_CARD_CUSTOMER_UNIQUE_ID" noMargin />
              </Col>
              <Col md={4}>
                <Input
                  id="customer-unique-id"
                  required
                  value={customerUniqueID}
                  placeholder={counterpart("CHANGE_CARD_CUSTOMER_UNIQUE_ID")}
                  onChange={(evt: any) => setState({ ...state, customerUniqueID: evt.target.value })}
                />
              </Col>
              <Col md={1}>
                <FormLabel target="customer-unique-id" content="CHANGE_CARD_COMPANY_ID" noMargin />
              </Col>
              <Col md={5}>
                <Input
                  id="company-id"
                  type="number"
                  required
                  value={companyID}
                  placeholder={counterpart("CHANGE_CARD_COMPANY_ID")}
                  onChange={(evt: any) => setState({ ...state, companyID: evt.target.value })}
                />
              </Col>
            </Row>
          </FormGroup>

          <PaymentDetailsForm
            onChangeCardholderName={(name) => setState({ ...state, cardholderName: name })}
            onChangeExpiryMonth={(month) => setState({ ...state, expiryMonth: month })}
            onChangeExpiryYear={(year) => setState({ ...state, expiryYear: year })}
            cardNumberId="spreedly-number-1"
            cvvId="spreedly-cvv-1"
            formData={paymentDetailsFormData}
            formErrors={cardDetailsErrors}
          />

          <Button
            id="send-request"
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
            title="CHANGE_CARD_BTN"
            isFullWidth
          />
        </form>
      </Panel>

      {/*<ErrorNotification
        title={apiError || counterpart("CHANGE_CARD_GENERIC_ERROR")}
        shouldTranslateTitle={false}
        showAlert={!!apiError || (!!cardDetailsErrors && Object.values(cardDetailsErrors).some(el => el))}
      />*/}

    </PageWrapper>
  )
}

export default ChangeCard;